/**
 * Reward balances are stored as points on the backend, where one point is
 * worth one cent (100 points = $1), and customers see them as points. Payout
 * copy (what a redemption pays out) stays in dollars.
 */
export const POINTS_PER_DOLLAR = 100

/** Smallest amount a user can redeem, in points ($5.00) */
export const MIN_REDEMPTION_POINTS = 500

/** Custom redemption amounts move in steps of this many points ($1.00) */
export const REDEMPTION_STEP_POINTS = 100

type FormatPointsOptions = {
  /** Prefix positive amounts with "+" (negative amounts always get "-") */
  signed?: boolean
  /** Append the "pts" unit. Defaults to true */
  unit?: boolean
}

/**
 * Formats a number of points for display, e.g. `1,000 pts`, `+2,500 pts`,
 * `-500 pts`. Fractional points (from sub-cent program rates) keep up to two
 * decimals.
 */
export function formatPoints(
  points: number,
  { signed = false, unit = true }: FormatPointsOptions = {},
): string {
  const magnitude = Math.abs(points).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  })
  const isZero = magnitude === '0'
  const sign = isZero ? '' : points < 0 ? '-' : signed ? '+' : ''
  const suffix = unit ? (magnitude === '1' ? ' pt' : ' pts') : ''
  return `${sign}${magnitude}${suffix}`
}

/**
 * Converts a dollar amount (as the program rewards API reports it) to points,
 * rounded to two decimals to avoid float noise like 7.000000000000001.
 */
export function dollarsToPoints(dollars: number): number {
  return Math.round(dollars * POINTS_PER_DOLLAR * 100) / 100
}

/**
 * Moves a custom redemption amount one step up or down, keeping it between
 * the redemption minimum and the user's balance. Returns the current amount
 * unchanged when the step would leave that range.
 */
export function stepRedemptionPoints(
  current: number,
  direction: 'minus' | 'plus',
  totalPoints: number,
): number {
  const next =
    direction === 'plus'
      ? current + REDEMPTION_STEP_POINTS
      : current - REDEMPTION_STEP_POINTS
  if (next < MIN_REDEMPTION_POINTS || next > totalPoints) return current
  return next
}
